import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import SiteDetail from "@/pages/SiteDetail";
import VisitList from "@/pages/VisitList";
import VisitEditor from "@/pages/VisitEditor";
import Care from "@/pages/Care";
import CareDetail from "@/pages/CareDetail";
import CareAnomaly from "@/pages/CareAnomaly";
import TrackList from "@/pages/TrackList";
import ClientRequests from "@/pages/ClientRequests";
import TrackDetail from "@/pages/TrackDetail";
import ClientPortal from "@/pages/ClientPortal";
import ClientSpace from "@/pages/ClientSpace";
import Report from "@/pages/Report";
import Devis from "@/pages/Devis";
import Stats from "@/pages/Stats";
import Pricing from "@/pages/Pricing";
import Settings from "@/pages/Settings";
import Planning from "@/pages/Planning";

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0D2E24]">
      <span className="signa-wordmark animate-pulse text-2xl text-white">
        SIGNA <span className="text-emerald-500">SYSTEM</span>
      </span>
    </div>
  );
}

// Routes équipe : un visiteur non authentifié est renvoyé vers la page de connexion équipe.
function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/equipe" replace />;
  return <Layout>{children}</Layout>;
}

// Racine (app.signaclean.fr) : espace de travail si connecté, sinon entrée orientée client.
function Home() {
  const { user, ready } = useAuth();
  if (!ready) return <Loading />;
  return user ? <Layout><Dashboard /></Layout> : <Navigate to="/espace-client" replace />;
}

// Conserve les anciens liens (connexion, invitations, devis) en préservant les paramètres.
function LegacyRedirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function AppRoutes() {
  const { user, ready } = useAuth();
  if (!ready) return <Loading />;
  return (
    <Routes>
      <Route path="/suivi/:token" element={<ClientPortal />} />
      <Route path="/suivi" element={<ClientPortal />} />
      <Route path="/espace-client" element={<ClientSpace />} />
      <Route path="/espace" element={<LegacyRedirect to="/espace-client" />} />
      <Route path="/equipe" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/login" element={<LegacyRedirect to="/equipe" />} />
      <Route path="/" element={<Home />} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/clients/:id" element={<Protected><Clients /></Protected>} />
      <Route path="/clients/:id/site/:sid" element={<Protected><SiteDetail /></Protected>} />
      <Route path="/check" element={<Protected><VisitList /></Protected>} />
      <Route path="/check/:id" element={<Protected><VisitEditor /></Protected>} />
      <Route path="/care" element={<Protected><Care /></Protected>} />
      <Route path="/care/anomalies/:id" element={<Protected><CareAnomaly /></Protected>} />
      <Route path="/care/:id" element={<Protected><CareDetail /></Protected>} />
      <Route path="/track" element={<Protected><TrackList /></Protected>} />
      <Route path="/track/:id" element={<Protected><TrackDetail /></Protected>} />
      <Route path="/planning" element={<Protected><Planning /></Protected>} />
      <Route path="/demandes" element={<Protected><ClientRequests /></Protected>} />
      <Route path="/devis" element={<Protected><Devis /></Protected>} />
      <Route path="/report" element={<Protected><Report /></Protected>} />
      <Route path="/report/:id" element={<Protected><Report /></Protected>} />
      <Route path="/stats" element={<Protected><Stats /></Protected>} />
      <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
